// frontend/src/features/ups/modals/MoveSiteModal.js
import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectCompanySites,
  selectSelectedSiteId,
} from '../../sites/sitesSlice';
import { moveAssignment } from '../../../services/assignmentService';
import { loadSiteAssignments } from '../../assignments/assignmentsSlice';
import { bumpStatsRefreshKey } from '../../ui/filtersSlice';

export default function MoveSiteModal({
  driverData,
  assignmentContext,
  onClose,
  onMoveSuccess,
  refreshAssignmentsAfterMove = true,
}) {
  const dispatch = useDispatch();

  const companySites = useSelector(selectCompanySites);
  const selectedSiteId = useSelector(selectSelectedSiteId);

  // RESTORED: targetSiteId state
  const [targetSiteId, setTargetSiteId] = useState('');
  
  const [targetStatus, setTargetStatus] = useState(
    String(assignmentContext?.assignment_status || 'confirmed').toLowerCase()
  );
  const [isBusy, setIsBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const assignmentId = assignmentContext?.assignment_id ?? null;
  const fromSiteId = assignmentContext?.site_id ?? selectedSiteId ?? null;
  
  // UI FIX: Site Name Fallback
  const fromSiteName =
    assignmentContext?.site_name ||
    (fromSiteId ? `Site #${fromSiteId}` : null);
    
  const currentStatus = String(assignmentContext?.assignment_status || '').toLowerCase();

  const availableSites = useMemo(() => {
    const currentId = Number(fromSiteId);
    return (companySites || []).filter((site) => Number(site?.id) !== currentId);
  }, [companySites, fromSiteId]);

  const safe = (v) => v ?? '—';

  const canSubmit =
    !!assignmentId &&
    !!fromSiteId &&
    !!targetSiteId &&
    Number(targetSiteId) !== Number(fromSiteId) &&
    !isBusy;

  async function handleSubmit(e) {
    e.preventDefault();

    if (!assignmentId) {
      setErrorMsg('No assignment is available to move.');
      return;
    }
    if (!fromSiteId) {
      setErrorMsg('Current site context is missing.');
      return;
    }
    if (!targetSiteId) {
      setErrorMsg('Please choose a target site.');
      return;
    }
    if (Number(targetSiteId) === Number(fromSiteId)) {
      setErrorMsg('Choose a different site.');
      return;
    }

    try {
      setIsBusy(true);
      setErrorMsg('');

      await moveAssignment(fromSiteId, assignmentId, {
        target_site_id: Number(targetSiteId),
        target_status: targetStatus || currentStatus || 'confirmed',
      });

      if (refreshAssignmentsAfterMove && selectedSiteId) {
        await dispatch(
          loadSiteAssignments({
            siteId: selectedSiteId,
            statuses: ['requested', 'confirmed', 'active'],
          })
        );
      }

      dispatch(bumpStatsRefreshKey());
      onMoveSuccess?.();
    } catch (err) {
      setErrorMsg(
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Could not move the assignment to the selected site.'
      );
    } finally {
      setIsBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      // UI FIX: Prevent backdrop click while busy
      onClick={() => {
        if (!isBusy) onClose?.();
      }}
      aria-hidden="true"
    >
      <div
        className="bg-white rounded-lg shadow-md p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="move-site-title"
      >
        <div className="flex justify-between items-center mb-4">
          <h2 id="move-site-title" className="text-xl font-semibold">
            Move Site
          </h2>
          <button
            onClick={onClose}
            disabled={isBusy}
            aria-label="Close move site modal"
            className="text-gray-500 hover:text-gray-700 text-xl font-bold"
          >
            &times;
          </button>
        </div>

        {errorMsg && (
          <div className="mb-3 p-2 rounded bg-red-50 text-red-700 text-sm border border-red-200">
            {errorMsg}
          </div>
        )}

        <div className="space-y-4 text-sm">
          <div className="rounded border border-gray-200 bg-gray-50 p-3 space-y-1">
            <p><strong>Driver:</strong> {safe(driverData?.name)}</p>
            <p>
              <strong>Current Status:</strong>{' '}
              <span className="capitalize">{safe(assignmentContext?.assignment_status)}</span>
            </p>
            <p><strong>Current Site:</strong> {safe(fromSiteName)}</p>
            <p><strong>Assignment ID:</strong> {safe(assignmentId)}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="move-site-target-site" className="block font-medium mb-1">
                Move To Site
              </label>
              <select
                id="move-site-target-site"
                name="targetSiteId"
                value={targetSiteId}
                onChange={(e) => setTargetSiteId(e.target.value)}
                disabled={isBusy}
                className="w-full border border-gray-300 rounded px-3 py-2 bg-white"
              >
                <option value="">Select a site</option>
                {availableSites.map((site) => (
                  <option key={site.id} value={site.id}>
                    {site.name || site.site_name || `Site #${site.id}`}
                  </option>
                ))}
              </select>

              {availableSites.length === 0 && (
                <p className="mt-2 text-xs text-gray-500">
                  No other company sites are available for this move.
                </p>
              )}
            </div>

            <div>
              <label htmlFor="move-site-target-status" className="block font-medium mb-1">
                Status After Move
              </label>
              <select
                id="move-site-target-status"
                name="targetStatus"
                value={targetStatus}
                onChange={(e) => setTargetStatus(e.target.value)}
                disabled={isBusy}
                className="w-full border border-gray-300 rounded px-3 py-2 bg-white"
              >
                <option value="requested">Requested</option>
                <option value="confirmed">Confirmed</option>
                <option value="active">Active</option>
              </select>
            </div>

            <div className="rounded border border-blue-200 bg-blue-50 p-3 text-xs text-blue-800">
              This moves the current open assignment to another site using the existing UPS move-site backend flow.
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={isBusy}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!canSubmit}
                className={`px-4 py-2 rounded text-white ${
                  canSubmit
                    ? 'bg-indigo-600 hover:bg-indigo-700'
                    : 'bg-gray-400 cursor-not-allowed'
                }`}
              >
                {isBusy ? 'Moving…' : 'Confirm Move'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
